import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BuyerForm from './BuyerForm';
import ShippingSelector from './ShippingSelector';
import { showError } from '../../utils/sweetAlert';
import { setWholesaleConfig, clearCart } from '../../store/cartSlice';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { saveCheckoutSnapshot, trackInitiateCheckout } from '../../utils/metaPixel';

const RESERVA_PAYMENT_OPTIONS = [
  {
    value: 'efectivo',
    label: 'Efectivo en local',
    description: 'Pagás en efectivo al momento de retirar el pedido.',
    icon: 'fa-money-bill-wave',
    surcharge: 0
  },
  {
    value: 'transferencia',
    label: 'Transferencia (BROU / Prex)',
    description: 'Transferencia bancaria o Prex. Te enviamos los datos al confirmar.',
    icon: 'fa-building-columns',
    surcharge: 0
  },
  {
    value: 'mercadopago_debito',
    label: 'Link de MercadoPago Débito',
    description: 'Te enviamos un link de pago con tarjeta de débito. Recargo: +7,89%.',
    icon: 'fa-credit-card',
    surcharge: 0.0789
  },
  {
    value: 'mercadopago_credito',
    label: 'Link de MercadoPago Crédito',
    description: 'Te enviamos un link de pago con tarjeta de crédito. Recargo: +11,54%.',
    icon: 'fa-credit-card',
    surcharge: 0.1154
  }
];

const Checkout = () => {
  const cart = useSelector((state) => state.cart.items);
  const subtotal = useSelector((state) => state.cart.total);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const checkoutTracked = useRef(false);

  useEffect(() => {
    if (checkoutTracked.current || cart.length === 0) return;
    checkoutTracked.current = true;
    trackInitiateCheckout(cart, subtotal);
  }, [cart, subtotal]);

  useEffect(() => {
    const loadWholesaleConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/wholesale-config`);
        const data = await response.json();

        if (response.ok && data.data) {
          dispatch(setWholesaleConfig(data.data));
        }
      } catch (error) {
        console.error('Error cargando precios por mayor:', error);
      }
    };

    loadWholesaleConfig();
  }, [dispatch]);

  // Estado para almacenar los datos del comprador desde BuyerForm
  const [buyerData, setBuyerData] = useState({
    nombre: '',
    mail: '',
    cedula: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    codigoPostal: '',
    telefono: '',
    referencias: ''
  });

  // Estado para el tipo de envío
  const [shippingOption, setShippingOption] = useState({
    type: 'retiro_local',
    label: 'Retiro en Local',
    cost: 0,
    estimatedDays: 'Disponible en 24hs'
  });

  // Estado para notas adicionales
  const [notes, setNotes] = useState('');

  // Estado para errores de validación del formulario
  const [errors, setErrors] = useState({});

  // Modo de checkout (mercadopago o reserva)
  const [checkoutMode, setCheckoutMode] = useState('mercadopago');
  // Método de pago seleccionado en modo reserva
  const [paymentMethod, setPaymentMethod] = useState('efectivo');

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/store-config`)
      .then(r => r.json())
      .then(data => {
        if (data?.data?.checkoutMode) {
          setCheckoutMode(data.data.checkoutMode);
        }
      })
      .catch(() => {});
  }, []);

  // Calcular total con envío y recargo de método de pago (reserva)
  const total = useMemo(() => {
    const base = subtotal + (shippingOption?.cost || 0);
    if (checkoutMode === 'reserva') {
      const option = RESERVA_PAYMENT_OPTIONS.find(o => o.value === paymentMethod);
      const surcharge = option?.surcharge || 0;
      return surcharge > 0 ? Math.round(base * (1 + surcharge)) : base;
    }
    return base;
  }, [subtotal, shippingOption, checkoutMode, paymentMethod]);

  const selectedPaymentOption = useCallback(() =>
    RESERVA_PAYMENT_OPTIONS.find(o => o.value === paymentMethod),
  [paymentMethod]);

  const handleConfirmOrder = async () => {
    const { nombre, mail, cedula, direccion, ciudad, departamento, codigoPostal, telefono, referencias } = buyerData;
    
    const newErrors = {};
    let errorMessage = '';

    // Validar campos obligatorios
    if (!nombre) newErrors.nombre = true;
    if (!mail) newErrors.mail = true;
    if (!cedula) newErrors.cedula = true;

    // Validar correo con una expresión regular
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (mail && !emailRegex.test(mail)) {
      newErrors.mail = true;
      if (!errorMessage) errorMessage = 'Por favor, ingrese un correo electrónico válido.';
    }

    // Validar cédula (ejemplo: debe ser numérica y tener entre 7 y 10 dígitos)
    const cedulaRegex = /^\d{7,10}$/;
    if (cedula && !cedulaRegex.test(cedula)) {
      newErrors.cedula = true;
      if (!errorMessage) errorMessage = 'Por favor, ingrese una cédula válida (entre 7 y 10 dígitos).';
    }

    // Validar campos de dirección (excepto si es retiro en local)
    const isPickup = ['retiro_local'].includes(shippingOption.type);
    if (!isPickup) {
      if (!direccion) newErrors.direccion = true;
      if (!ciudad) newErrors.ciudad = true;
      if (!telefono) newErrors.telefono = true;
    } else {
      // Para retiro en local solo necesitamos teléfono
      if (!telefono) newErrors.telefono = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      
      if (!errorMessage) {
        if (newErrors.nombre || (!mail && newErrors.mail) || (!cedula && newErrors.cedula)) {
          errorMessage = 'Por favor, complete todos los datos personales.';
        } else if (newErrors.direccion || newErrors.ciudad || newErrors.telefono) {
          if (!['retiro_local'].includes(shippingOption.type)) {
            errorMessage = 'Por favor, complete todos los campos de dirección de envío.';
          } else {
            errorMessage = 'Por favor, ingrese un teléfono de contacto.';
          }
        }
      }
      
      showError(errorMessage);
      return;
    }
    
    setErrors({});

    const isRetiro = ['retiro_local'].includes(shippingOption.type);

    try {
      // Preparar items del carrito para enviar
      const items = cart.map(item => ({
        _id: item._id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        selectedVariant: item.selectedVariant,
        variantInfo: item.variantInfo || null,
        selectedSizes: item.selectedSizes || [],
        selectedColors: item.selectedColors || [],
        images: item.images,
        image: item.image
      }));

      if (checkoutMode === 'reserva') {
        const orderPayload = {
          items: items.map(item => ({
            _id: item._id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            variant: item.selectedVariant
              ? { size: item.selectedVariant.size, color: item.selectedVariant.color, sku: item.selectedVariant.sku }
              : undefined,
            variantInfo: item.variantInfo || undefined,
            selectedSizes: item.selectedSizes,
            selectedColors: item.selectedColors,
            image: item.selectedVariant?.images?.[0] || item.images?.[0] || item.image
          })),
          subtotal,
          total,
          buyerInfo: { nombre, mail, cedula, telefono },
          shippingAddress: {
            direccion: isRetiro ? 'Retiro en local' : direccion,
            ciudad: isRetiro ? 'Montevideo' : ciudad,
            departamento: departamento || '',
            codigoPostal: codigoPostal || '',
            telefono,
            referencias: referencias || ''
          },
          shipping: {
            type: shippingOption.type,
            label: shippingOption.label,
            cost: shippingOption.cost,
            estimatedDays: shippingOption.estimatedDays || ''
          },
          payment: { method: paymentMethod, status: 'pending' },
          notes
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/orders`,
          orderPayload
        );

        const pOpt = selectedPaymentOption();
        dispatch(clearCart());
        navigate('/success', {
          state: {
            orderId: response.data._id,
            orderNumber: response.data.orderNumber,
            items,
            subtotal,
            total,
            shippingOption,
            buyerInfo: { nombre, mail, cedula, telefono },
            paymentMethodInfo: { label: pOpt?.label || 'Reserva / Pago manual', method: paymentMethod },
            notes
          }
        });
        return;
      }

      const preferenceData = {
        items,
        subtotal,
        shippingType: shippingOption.type,
        shippingCost: shippingOption.cost,
        total,
        nombre,
        mail,
        cedula,
        direccion: isRetiro ? 'Retiro en local' : direccion,
        ciudad: isRetiro ? 'Montevideo' : ciudad,
        departamento,
        codigoPostal,
        telefono,
        referencias,
        notes
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/mp/create_preference`,
        preferenceData
      );

      if (response.data.init_point) {
        saveCheckoutSnapshot({
          preferenceId: response.data.id,
          items,
          subtotal,
          total,
          paymentMethod: 'mercadopago'
        });
        window.location.href = response.data.init_point;
      } else {
        showError("Error al procesar el pago");
      }
    } catch (error) {
      showError('Ocurrió un error al procesar el pago');
    }
  };

  const handleBackToCart = () => {
    navigate('/cart');
  };

  return (
    <div className="container wrapper mt-5">
      {cart.length === 0 ? (
        <div className="checkout-empty">
          <div className="text-center py-5">
            <i className="fa-solid fa-cart-shopping" style={{ fontSize: '4rem', color: '#ccc', marginBottom: '20px' }}></i>
            <h2 className="mb-3">No hay productos en tu carrito</h2>
            <p className="text-muted mb-4">Agrega algunos productos antes de proceder al checkout</p>
            <button 
              className="add-to-cart-btn px-5 py-3 shadow-sm"
              style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold' }}
              onClick={() => navigate('/tienda')}
            >
              <i className="fa-solid fa-store me-2"></i>
              Ir a la tienda
            </button>
          </div>
        </div>
      ) : (
        <div className="checkout-container">
          {/* Header */}
          <div className="checkout-header">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h1 className="checkout-title">
                  <i className="fa-solid fa-credit-card me-3"></i>
                  Finalizar Compra
                </h1>
                <p className="checkout-subtitle">
                  Revisa tu pedido y completa tus datos
                </p>
              </div>
              <div className="col-md-6 text-md-end">
                <div className="checkout-progress">
                  <span className="progress-step active">1. Revisión</span>
                  <span className="progress-arrow">→</span>
                  <span className="progress-step active">2. Datos</span>
                  <span className="progress-arrow">→</span>
                  <span className="progress-step">3. Pago</span>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Order Summary */}
            <div className="col-lg-7">
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-list me-2"></i>
                    Resumen del Pedido
                  </h4>
                  <span className="item-count">
                    {cart.reduce((acc, item) => acc + item.quantity, 0)} productos
                  </span>
                </div>
                
                <div className="order-items">
                  {cart.map((item) => (
                    <div key={`${item._id}-${item.selectedVariant?.sku || 'no-variant'}`} className="checkout-item">
                      <div className="row align-items-center">
                        <div className="col-2">
                          <img 
                            src={getDisplayImageUrl(item.selectedVariant?.images?.[0] || item.images?.[0] || item.image)} 
                            alt={item.name}
                            className="checkout-item-image"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/80?text=Sin+Imagen';
                            }}
                          />
                        </div>
                        <div className="col-6">
                          <h6 className="item-name">{item.name}</h6>
                          {item.selectedVariant && (
                            <div className="item-variants">
                              {item.selectedVariant.size && <span className="variant-tag size">{item.selectedVariant.size}</span>}
                              {item.selectedVariant.color && <span className="variant-tag color">{item.selectedVariant.color}</span>}
                              {item.selectedVariant.productType && <span className="variant-tag">{item.selectedVariant.productType}</span>}
                            </div>
                          )}
                          {!item.selectedVariant && item.variantInfo && (
                            <div className="item-variants">
                              <span className="variant-tag">{item.variantInfo}</span>
                            </div>
                          )}
                          {item.selectedSizes?.length > 0 && (
                            <div className="item-variants">
                              {item.selectedSizes.map((s, i) => <span key={i} className="variant-tag size">{s}</span>)}
                            </div>
                          )}
                          {item.selectedColors?.length > 0 && (
                            <div className="item-variants">
                              {item.selectedColors.map((c, i) => <span key={i} className="variant-tag color">{c}</span>)}
                            </div>
                          )}
                          {item.selectedVariant?.sku && (
                            <small className="item-sku">SKU: {item.selectedVariant.sku}</small>
                          )}
                        </div>
                        <div className="col-2 text-center">
                          <span className="item-quantity">×{item.quantity}</span>
                        </div>
                        <div className="col-2 text-end">
                          <span className="item-price">${item.price * item.quantity}</span>
                          <small className="item-unit-price">${item.price} c/u</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Selector */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-truck me-2"></i>
                    Método de Envío
                  </h4>
                </div>
                <ShippingSelector 
                  selectedShipping={shippingOption.type}
                  onShippingChange={setShippingOption}
                  ciudad={buyerData.ciudad}
                />
              </div>

              {/* Buyer Form */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-user me-2"></i>
                    Datos del Comprador
                  </h4>
                </div>
                <BuyerForm 
                  onChange={(data) => {
                    setBuyerData(data);
                    if (Object.keys(errors).length > 0) {
                      setErrors({});
                    }
                  }} 
                  shippingType={shippingOption.type}
                  errors={errors}
                />
              </div>

              {/* Método de pago (solo en modo reserva) */}
              {checkoutMode === 'reserva' && (
                <div className="checkout-section">
                  <div className="section-header">
                    <h4>
                      <i className="fa-solid fa-wallet me-2"></i>
                      Método de Pago
                    </h4>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {RESERVA_PAYMENT_OPTIONS.map(opt => {
                      const isSelected = paymentMethod === opt.value;
                      return (
                        <div
                          key={opt.value}
                          onClick={() => setPaymentMethod(opt.value)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                            padding: '14px 16px',
                            border: `1px solid ${isSelected ? 'var(--brand-primary, #1a1a1a)' : 'var(--border-light, #e0dbd5)'}`,
                            borderLeft: isSelected ? '4px solid var(--brand-primary, #1a1a1a)' : '4px solid transparent',
                            background: isSelected ? 'var(--bg-secondary, #f8f5f2)' : '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.15s'
                          }}
                        >
                          <div style={{
                            width: 40, height: 40, borderRadius: 6,
                            background: isSelected ? 'var(--brand-primary, #1a1a1a)' : 'var(--bg-secondary, #f0ece8)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <i className={`fas ${opt.icon}`} style={{ color: isSelected ? '#fff' : 'var(--text-muted, #777)', fontSize: '1rem' }}></i>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary, #1a1a1a)', marginBottom: 2 }}>
                              {opt.label}
                            </div>
                            <div style={{ fontFamily: 'var(--font-body, Inter, sans-serif)', fontSize: '0.78rem', color: 'var(--text-muted, #888)', fontWeight: 300 }}>
                              {opt.description}
                            </div>
                          </div>
                          {isSelected && (
                            <div style={{
                              width: 24, height: 24, borderRadius: '50%',
                              background: 'var(--brand-primary, #1a1a1a)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <i className="fas fa-check" style={{ color: '#fff', fontSize: '0.65rem' }}></i>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notas adicionales */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-comment me-2"></i>
                    Notas del Pedido (opcional)
                  </h4>
                </div>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Instrucciones especiales, horarios de entrega preferidos, etc."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Payment Summary */}
            <div className="col-lg-5">
              <div className="checkout-sidebar">
                <div className="payment-summary">
                  <div className="summary-header">
                    <h4>
                      <i className="fa-solid fa-calculator me-2"></i>
                      Total del Pedido
                    </h4>
                  </div>
                  
                  <div className="summary-content">
                    <div className="summary-row">
                      <span>Subtotal ({cart.reduce((acc, item) => acc + item.quantity, 0)} productos)</span>
                      <span>${subtotal}</span>
                    </div>
                    <div className="summary-row">
                      <span>
                        <i className="fa-solid fa-truck me-1"></i>
                        {shippingOption.label}
                      </span>
                      {shippingOption.cost === 0 ? (
                        <span className="free-text">Gratis</span>
                      ) : (
                        <span>${shippingOption.cost}</span>
                      )}
                    </div>
                    <div className="summary-row shipping-time">
                      <small className="text-muted">
                        <i className="fa-regular fa-clock me-1"></i>
                        {shippingOption.estimatedDays}
                      </small>
                    </div>
                    <div className="summary-row">
                      <span>Impuestos</span>
                      <span>Incluidos</span>
                    </div>
                    {checkoutMode === 'reserva' && (() => {
                      const pOpt = RESERVA_PAYMENT_OPTIONS.find(o => o.value === paymentMethod);
                      return pOpt?.surcharge > 0 ? (
                        <div className="summary-row" style={{ color: 'var(--text-muted, #888)', fontSize: '0.83rem' }}>
                          <span>Recargo ({pOpt.label})</span>
                          <span>+{(pOpt.surcharge * 100).toFixed(2).replace('.', ',')}%</span>
                        </div>
                      ) : null;
                    })()}
                    <div className="summary-divider"></div>
                    <div className="summary-row total">
                      <strong>Total a Pagar</strong>
                      <strong className="total-amount">${total.toLocaleString('es-UY')}</strong>
                    </div>
                  </div>

                  <div className="payment-actions">
                    <button 
                      className="add-to-cart-btn w-100 py-3 mb-3"
                      onClick={handleConfirmOrder}
                    >
                      {checkoutMode === 'reserva' ? (
                        <><i className="fa-solid fa-clock me-2"></i>CONFIRMAR PEDIDO</>
                      ) : (
                        <><i className="fa-solid fa-credit-card me-2"></i>PAGAR AHORA</>
                      )}
                    </button>
                    
                    <button 
                      className="btn btn-outline-secondary w-100 py-2"
                      onClick={handleBackToCart}
                    >
                      <i className="fa-solid fa-arrow-left me-2"></i>
                      Volver al Carrito
                    </button>
                  </div>

                  <div className="security-badges">
                    <div className="security-item">
                      <i className="fa-solid fa-shield-halved"></i>
                      <span>Compra Segura</span>
                    </div>
                    <div className="security-item">
                      <i className="fa-solid fa-credit-card"></i>
                      <span>Mercado Pago</span>
                    </div>
                    <div className="security-item">
                      <i className="fa-solid fa-undo"></i>
                      <span>Garantía de devolución</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
